window.theme = window.theme || {};

class PdpCartDrawer extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.onSelectionUpdate = this.onSelectionUpdate.bind(this);
    this._kitVisible = false;

    this.addEventListener('click', this.onClick);
    const selectionEvent = theme.pubsub?.PUB_SUB_EVENTS?.bundleSelectionChange;
    if (selectionEvent) {
      this.selectionUnsubscriber = theme.pubsub.subscribe(
        selectionEvent,
        this.onSelectionUpdate
      );
    }

    const picker = this.picker;
    if (picker?.getDrawerPayload) this.renderPayload(picker.getDrawerPayload());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.selectionUnsubscriber) this.selectionUnsubscriber();
  }

  get box() {
    return this.querySelector('[data-pdp-cart-box]');
  }

  get list() {
    return this.querySelector('[data-pdp-cart-list]');
  }

  get checkoutButton() {
    return this.querySelector('[data-pdp-cart-checkout]');
  }

  get errorElement() {
    return this.querySelector('[data-pdp-cart-error]');
  }

  get picker() {
    return document.querySelector('product-bundle-picker');
  }

  onClick(event) {
    if (event.target.closest('[data-pdp-cart-toggle]') && window.innerWidth <= 1023) {
      this.toggleMobile();
      return;
    }

    const removeButton = event.target.closest('[data-pdp-remove]');
    if (removeButton) {
      event.preventDefault();
      const item = removeButton.closest('[data-pdp-item]');
      if (!item) return;
      this.picker?.uncheckVariant?.(item.dataset.variantId);
      return;
    }

    const checkoutButton = event.target.closest('[data-pdp-cart-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      this.picker?.submitBundle?.(event);
    }
  }

  toggleMobile() {
    this.box?.classList.toggle('is-open');
  }

  showKit() {
    if (this._kitVisible) return;
    this._kitVisible = true;

    const product = this.closest('.product');
    requestAnimationFrame(() => {
      product?.classList.add('is-kit-visible');
      this.classList.add('is-kit-visible');
    });
  }

  onSelectionUpdate(event) {
    if (!event || event.source !== 'product-bundle-picker') return;
    this.renderPayload(event);
  }

  formatMoney(cents) {
    if (window.theme?.utils?.formatMoney) {
      return theme.utils.formatMoney(cents, theme.settings.moneyFormat);
    }
    return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
  }

  drawerImageUrl(url) {
    if (!url) return '';
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=140');
    }
    return url;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  itemPriceHtml(item) {
    const price = Number(item.price) || 0;
    const compareAt = Number(item.compareAt) || 0;
    if (item.isFree || compareAt > price) {
      return `<span class="pdp-cart__strike">${this.formatMoney(compareAt)}</span><span>${this.formatMoney(price)}</span>`;
    }
    return this.formatMoney(price);
  }

  itemHtml(item) {
    const title = this.escapeHtml(item.title || '');
    const image = this.drawerImageUrl(item.imageUrl);
    const preorder = (item.preorderDate || '').trim();
    const remove = item.isFree ? '' : `
      <div class="pdp-cart__item-actions">
        <button type="button" class="pdp-cart__remove" data-pdp-remove aria-label="Remove ${title}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/><path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/></svg>
        </button>
      </div>
    `;

    return `
      <div class="pdp-cart__item" data-pdp-item data-variant-id="${this.escapeHtml(item.variantId || '')}">
        <div class="pdp-cart__item-img-wrap">
          ${image ? `<img class="pdp-cart__item-img" src="${this.escapeHtml(image)}" alt="${title}" width="70" height="70" loading="lazy">` : ''}
        </div>
        <div class="pdp-cart__item-info">
          <h4 class="pdp-cart__item-title">${title}</h4>
          ${preorder ? `<div class="pdp-cart__item-preorder">Estimated Ship Date ${this.escapeHtml(preorder)}</div>` : ''}
          <div class="pdp-cart__item-price">${this.itemPriceHtml(item)}</div>
        </div>
        ${remove}
      </div>
    `;
  }

  renderPayload(payload) {
    if (!this.list) return;

    const items = payload?.items || [];
    const isEmpty = items.length === 0;

    if (isEmpty) {
      this.list.innerHTML = '<div class="pdp-cart__empty">Your bundle is empty. Select products to get started</div>';
    } else {
      this.list.innerHTML = items.map((item) => this.itemHtml(item)).join('');
    }

    this.updateTotals(payload || {});
    this.updateCheckoutState(!isEmpty);
    this.setError();
    this.removeAttribute('aria-busy');

    if (!isEmpty) this.showKit();
  }

  updateTotals(payload) {
    const hasDiscount = Boolean(payload.hasDiscount);
    const original = this.formatMoney(payload.compareAt || 0);
    const total = this.formatMoney(payload.total || 0);

    this.querySelectorAll('[data-pdp-cart-strike], [data-pdp-cart-mobile-strike]').forEach((el) => {
      el.toggleAttribute('hidden', !hasDiscount);
      if (hasDiscount) el.textContent = original;
    });

    this.querySelectorAll('[data-pdp-cart-total], [data-pdp-cart-mobile-total]').forEach((el) => {
      el.textContent = total;
    });
  }

  updateCheckoutState(hasItems) {
    const button = this.checkoutButton;
    if (!button) return;
    button.disabled = !hasItems;
    button.classList.toggle('is-active', hasItems);
  }

  setError(message) {
    const error = this.errorElement;
    if (!error) return;
    error.toggleAttribute('hidden', !message);
    error.textContent = message || '';
  }
}

if (!customElements.get('pdp-cart-drawer')) {
  customElements.define('pdp-cart-drawer', PdpCartDrawer);
}
